// The `spend-by-category` route retains its navigation shell while the
// canonical aggregate read is unavailable. Diagnostic transaction rows are
// deliberately not presented as a financial ranking.
import { useShellContext } from '@/app/shell/AppShell';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { useSpendByCategory } from '@/api/hooks/useSpendByCategory';
import { ViewSwitcher } from './ViewSwitcher';
import { getScopeDateBounds } from './transactionDateRange';
import { DateScopeGuard } from '@/app/shell/DateScopeGuard';

function CategoriesPageContent() {
  const {
    accountFilter,
    dateFrom: scopeFrom,
    dateTo: scopeTo,
    scopeAvailability,
    retryScopeAvailability,
  } = useShellContext();
  const { from: dateFrom, to: dateTo } = getScopeDateBounds({ dateFrom: scopeFrom, dateTo: scopeTo });
  useSpendByCategory({ accountId: accountFilter, dateFrom, dateTo, enabled: scopeAvailability === 'ready' });

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return <RouteRecoveryScreen kind="offline" frame="inline" />;
  }

  if (scopeAvailability !== 'ready') {
    return (
      <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        {scopeAvailability === 'pending' ? (
          'Checking transaction scope availability…'
        ) : (
          <>
            Transaction scope availability could not be checked.{' '}
            <button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>
              Retry
            </button>
          </>
        )}
      </p>
    );
  }

  return (
    <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
      Category totals are not available yet.
    </p>
  );
}

export function CategoriesPage() {
  const { dateScopeAvailability } = useShellContext();
  return (
    <section aria-labelledby="by-category-heading" className="mx-auto w-full max-w-5xl p-4">
      <h1 id="by-category-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
      <ViewSwitcher />
      <DateScopeGuard availability={dateScopeAvailability}>
        <CategoriesPageContent />
      </DateScopeGuard>
    </section>
  );
}

export default CategoriesPage;
